import React, { useState, useEffect, useRef } from "react";
import request from "axios";
import "../documentation.css";
import LoadingAnimation from "../loadingAnimation/loadingAnimation";
import DownloadAnimation from "../loadingAnimation/downloadAnimation";
import WoodlandDocuments from "./WoodDocuments";
import { convertNewLineToSpace } from "../helpers";
import LoadingError from "../../common/LoadingError/LoadingError";
import SearchBox from "../searchBox";

const NotFoundSearch = ({ searchValue }) => (
  <div className="bg-white w-100 p-2 d-flex justify-content-center align-items-center flex-column">
    <h4>Sorry, we couldn't find any matches for</h4>
    <h4 className="mb-1 documentation__search-notfound">{searchValue}</h4>
    <p>Please try searching with another term.</p>
  </div>
);

const DocumentationWood = ({ tokenStore, handleOverlay, passImage }) => {
  const token = tokenStore.getToken();
  const [searchValue, setSearchValue] = useState("");
  const [candidatesInChunks, setCandidatesInChunks] = useState([]);
  const [searchedCandidates, setSearchedCandidates] = useState();
  const [showLoadingAnimation, setShowLoadingAnimation] = useState(true);
  const [downloadAnimation, setDownloadAnimation] = useState(false);
  const [paragraphBehindBoard, setParagraphBehindBoard] = useState();
  const [pickedPage, setPickedPage] = useState(1);
  const pickedCandidateChunk = candidatesInChunks.find(
    candidate => candidate.page === pickedPage
  );
  const totalPages =
    (candidatesInChunks[0] && candidatesInChunks[0].totalPages) || 0;
  const isPrevActive = () => pickedPage > 1;
  const isNextActive = () => candidatesInChunks[0] && pickedPage < totalPages;
  const downloadRef = useRef();
  const getDocs = () => searchedCandidates || pickedCandidateChunk.docs;
  const getImages = () => pickedCandidateChunk && pickedCandidateChunk.images;

  const getReadingListPdf = async (candidateRef, firstName, lastName) => {
    setDownloadAnimation(true);
    const response = await request.get(
      `${process.env.REACT_APP_PROFILE_API_URL}/clientDashboard/candidate/checklist/induction?candidateRef=${candidateRef}&formName=Woodland_Induction.pdf&typeCode=YWLG&formId=readingListWoodland`,
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      }
    );

    if (response.status === 200) {
      setDownloadAnimation(false);
      const blob = new Blob([response.data], {
        type: "application/pdf"
      });
      downloadRef.current.href = window.URL.createObjectURL(blob);
      downloadRef.current.download = `${convertNewLineToSpace(
        `Woodland Induction`
      )} ${firstName} ${lastName}.pdf`;
      downloadRef.current.click();
    }
  };

  const getRTWDocument = async (
    candidateRef,
    firstName,
    lastName,
    typeCode,
    filename,
    type,
    fileExt
  ) => {
    setDownloadAnimation(true);
    try {
      const response = await request.get(
        `${process.env.REACT_APP_PROFILE_API_URL}/clientDashboard/candidate/checklist/induction?candidateRef=${candidateRef}&typeCode=${typeCode}&formName=${filename}&formId=readingListWoodland`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );
      if (response.status === 200) {
        setDownloadAnimation(false);
        const blob = new Blob([response.data], {
          type
        });
        downloadRef.current.href = window.URL.createObjectURL(blob);
        downloadRef.current.download = `${convertNewLineToSpace(
          filename
        )} ${firstName} ${lastName}.${fileExt}`;
        downloadRef.current.click();
      }
    } catch (e) {
      console.error("=== Error Downloading RTW Document for Woodland ===");
      console.error(e);
    }
  };

  const fetchCandidates = async (name = "") => {
    const isSearch = name !== "";
    setParagraphBehindBoard(undefined);
    setShowLoadingAnimation(true);
    if (!pickedCandidateChunk || isSearch) {
      try {
        const { data } = await request.get(
          `${
            process.env.REACT_APP_PROFILE_API_URL
          }/clientDashBoard/candidate/onboardingDocsWoodland?page=${pickedPage}${
            isSearch ? `&filter=${name}` : ""
          }`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        if (isSearch) {
          if (!data.docs)
            setParagraphBehindBoard("No candidates match searched phrase.");
          setSearchedCandidates(data.docs);
        } else {
          if (!data.docs) setParagraphBehindBoard("You have no candidates");
          setCandidatesInChunks([
            ...candidatesInChunks,
            {
              page: pickedPage,
              docs: data.docs,
              totalPages: data.totalPages,
              searchPhrase: "",
              images: data.imagesObj
            }
          ]);
          setPickedPage(pickedPage);
        }
      } catch (err) {
        console.error("Failed to load candidates from server", err);
        setParagraphBehindBoard(
          <div className="w-100 d-flex justify-content-center">
            <LoadingError
              className="bg-white pt-2"
              errorText={"There was an error loading your information."}
              retryFunc={() => fetchCandidates(isSearch ? name : "")}
            />
          </div>
        );
      }
    }
    setShowLoadingAnimation(false);
  };

  const handleCloseSearchClick = () => {
    setSearchValue("");
    setSearchedCandidates(undefined);
  };

  useEffect(() => {
    fetchCandidates();
  }, [pickedPage]);

  return (
    <>
      {candidatesInChunks && (
        <div className="mb-2">
          <div className="d-flex w-100 justify-content-center flex-column align-items-center pb-3">
            <h2 className="tw-text-2xl tw-mb-4 tw-font-bold">
              Woodland Onboarding
            </h2>
          </div>
          {showLoadingAnimation && <LoadingAnimation />}
          {downloadAnimation && <DownloadAnimation />}
          {paragraphBehindBoard && paragraphBehindBoard}
          {!showLoadingAnimation &&
            !downloadAnimation &&
            pickedCandidateChunk &&
            pickedCandidateChunk.docs.length !== 0 &&
            !paragraphBehindBoard && (
              <>
                <form
                  className="documentation__search"
                  onSubmit={e => {
                    if (searchValue === "") {
                      handleCloseSearchClick();
                    }
                    fetchCandidates(searchValue);
                    e.preventDefault();
                  }}
                >
                  <SearchBox
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    placeholder={"Search"}
                    onCloseClick={handleCloseSearchClick}
                  />
                  <button className="documentation__search-button btn ml-hf px-hf px-md-2">
                    Search
                  </button>
                </form>
                <div>
                  <b
                    className={`documentation__search-results mt-1 mb-1 ${!searchedCandidates &&
                      "invisible"}`}
                  >
                    {searchedCandidates && searchedCandidates.length} result(s)
                    found
                  </b>
                </div>
                {getDocs().length === 0 && searchedCandidates ? (
                  <NotFoundSearch searchValue={searchValue} />
                ) : (
                  <>
                    <WoodlandDocuments
                      candidates={getDocs()}
                      getReadingListPdf={getReadingListPdf}
                      getRTWDocument={getRTWDocument}
                      downloadRef={downloadRef}
                      images={getImages()}
                      passImage={passImage}
                      handleOverlay={handleOverlay}
                    />
                    <a className="d-none" ref={downloadRef}></a>
                  </>
                )}
                {pickedCandidateChunk.docs &&
                  Number(totalPages) > 1 &&
                  !searchedCandidates && (
                    <div className="w-100 d-flex flex-row-reverse">
                      <div className="mt-1 mb-2 mr-1">
                        <span
                          className={`documentation-mobile__page ${!isPrevActive() &&
                            "documentation-mobile__page-disabled"}`}
                          onClick={
                            isPrevActive()
                              ? () => setPickedPage(pickedPage - 1)
                              : () => {}
                          }
                        >
                          {"<"}
                        </span>
                        {Array(totalPages)
                          .fill()
                          .map((_, index) => {
                            const currentPageNumber = index + 1;

                            if (
                              currentPageNumber === pickedPage - 1 ||
                              currentPageNumber === pickedPage + 1 ||
                              currentPageNumber === pickedPage
                            ) {
                              return (
                                <span
                                  className={`documentation-mobile__page ${pickedPage ===
                                    currentPageNumber &&
                                    "documentation-mobile__page-picked"}`}
                                  onClick={() => setPickedPage(index + 1)}
                                  key={index}
                                >
                                  {currentPageNumber}
                                </span>
                              );
                            }
                            if (currentPageNumber === totalPages) {
                              return (
                                <>
                                  <span className="documentation-mobile__page-dots">
                                    {"..."}
                                  </span>
                                  <span
                                    className={`documentation-mobile__page ${pickedPage ===
                                      currentPageNumber &&
                                      "documentation-mobile__page-picked"}`}
                                    onClick={() => setPickedPage(index + 1)}
                                    key={index}
                                  >
                                    {currentPageNumber}
                                  </span>
                                </>
                              );
                            }
                            if (currentPageNumber === 1) {
                              return (
                                <>
                                  <span
                                    className={`documentation-mobile__page ${pickedPage ===
                                      currentPageNumber &&
                                      "documentation-mobile__page-picked"}`}
                                    onClick={() => setPickedPage(index + 1)}
                                    key={index}
                                  >
                                    {currentPageNumber}
                                  </span>
                                  <span className="documentation-mobile__page-dots">
                                    {"..."}
                                  </span>
                                </>
                              );
                            }
                            return <></>;
                          })}
                        <span
                          className={`documentation-mobile__page ${!isNextActive() &&
                            "documentation-mobile__page-disabled"}`}
                          onClick={
                            isNextActive()
                              ? () => setPickedPage(pickedPage + 1)
                              : () => {}
                          }
                        >
                          {">"}
                        </span>
                      </div>
                    </div>
                  )}
              </>
            )}
        </div>
      )}
    </>
  );
};

export default DocumentationWood;
